import * as THREE from 'three';
import Delaunator from 'delaunator';
import { BufferGeometryUtils } from 'three/examples/jsm/Addons.js';
import { findTopBoundaryVertices, getBoundaryLoop, findTopmostVertex } from './boundaryUtils.js';
import { fixProblematicCurves } from './curveDetection.js';

/**
 * Top mesh generation for mould socket types
 * Following the algorithm specified in THICKNESS.md
 */

/**
 * Returns true if point `pt` lies inside the 2D polygon `poly`.
 * @param {THREE.Vector2} pt
 * @param {THREE.Vector2[]} poly
 * @returns {boolean}
 */
function pointInPolygon(pt, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x, yi = poly[i].y;
    const xj = poly[j].x, yj = poly[j].y;
    const intersect =
      ((yi > pt.y) !== (yj > pt.y)) &&
      (pt.x < ((xj - xi) * (pt.y - yi)) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * Generate a top mesh for mould socket type
 * @param {THREE.BufferGeometry} baseGeometry - The original socket geometry
 * @param {number} yOffset - Y offset to add above the topmost point (default: 2 units)
 * @param {Object} options - Options for mesh generation
 * @param {number} options.sideSubdivisions - Number of subdivisions for side walls (default: 3)
 * @param {number} options.maxEdgeLength - Maximum edge length for top face subdivision (default: 1.0)
 * @param {number} options.gridSpacing - Grid spacing for interior points (default: 0.5)
 * @param {number} options.smoothIterations - Number of smoothing iterations for top vertices (default: 5)
 * @param {number} options.smoothDamping - Smoothing damping factor 0-1 (default: 0.5)
 * @param {THREE.Scene} options.debugScene - Optional scene for debug visualization
 * @returns {THREE.BufferGeometry|null} Generated top mesh geometry or null if failed
 */
export function generateTopMesh(baseGeometry, yOffset = 2, options = {}) {
  if (!baseGeometry || !baseGeometry.attributes.position) {
    return null;
  }

  try {
    // Step 1: Find boundary vertices in the top section
    const boundaryVertices = findTopBoundaryVertices(baseGeometry);

    if (boundaryVertices.length === 0) {
      return null;
    }

    // Step 2: Get the complete boundary loop starting from the first boundary vertex
    const indices = baseGeometry.index ? baseGeometry.index.array : null;
    const boundaryLoop = getBoundaryLoop(baseGeometry, boundaryVertices[0], indices);

    if (boundaryLoop.length < 3) {
      return null;
    }

    // Step 2.5: Fix problematic curves in the boundary before vertical projection
    const curveFixOptions = {
      enterThreshold: options.curveEnterThreshold || 0.1,
      exitThreshold: options.curveExitThreshold || 0.95,
      minSegmentLength: options.curveMinSegmentLength || 3,
      // Forward debug scene so visualizers can render to it
      debugScene: options.debugScene
    };

    const { fixedGeometry, fixedBoundaryLoop, capInfo } = fixProblematicCurves(
      baseGeometry,
      boundaryLoop,
      curveFixOptions
    );

    if (capInfo.length > 0) {
      console.log(`[TopMeshGenerator] Applied ${capInfo.length} curve fix(es) to boundary`);
    }

    // Step 3: Create the top mesh using the fixed geometry and boundary
    const topMesh = createTopMeshFromBoundary(
      fixedGeometry,
      fixedBoundaryLoop,
      yOffset,
      boundaryVertices,
      options
    );

    // Step 4: If curve caps were created, merge them into the top mesh
    if (capInfo.length > 0 && topMesh) {
      console.log('[TopMeshGenerator] Merging curve caps into top mesh');
      const mergedMesh = mergeCapGeometryIntoTopMesh(fixedGeometry, topMesh, baseGeometry);
      return mergedMesh;
    }

    return topMesh;

  } catch (error) {
    console.error('[TopMeshGenerator] Error generating top mesh:', error);
    return null;
  }
}

/**
 * Find the topmost vertex among the boundary vertices
 * @param {THREE.BufferGeometry} baseGeometry - The mesh geometry
 * @param {Array} boundaryVertexIndices - Array of boundary vertex indices
 * @returns {Object} Object containing {index, position} of the topmost boundary vertex
 */
function findTopmostBoundaryVertex(baseGeometry, boundaryVertexIndices) {
  const positions = baseGeometry.attributes.position.array;

  let maxY = -Infinity;
  let topmostIndex = boundaryVertexIndices[0];

  for (const vertexIndex of boundaryVertexIndices) {
    const y = positions[vertexIndex * 3 + 1];
    if (y > maxY) {
      maxY = y;
      topmostIndex = vertexIndex;
    }
  }

  return {
    index: topmostIndex,
    position: new THREE.Vector3(
      positions[topmostIndex * 3],
      positions[topmostIndex * 3 + 1],
      positions[topmostIndex * 3 + 2]
    )
  };
}

/**
 * Extract bottom vertices from boundary loop
 * @param {Float32Array} positions - Position array
 * @param {Array} boundaryLoop - Array of vertex indices
 * @returns {Array} Array of Vector3 bottom vertices
 */
function extractBottomVertices(positions, boundaryLoop) {
  const bottomVertices = [];
  for (const vertexIndex of boundaryLoop) {
    const x = positions[vertexIndex * 3];
    const y = positions[vertexIndex * 3 + 1];
    const z = positions[vertexIndex * 3 + 2];
    bottomVertices.push(new THREE.Vector3(x, y, z));
  }
  return bottomVertices;
}

/**
 * Ensure loop vertices are ordered counter-clockwise in the XZ plane
 * @param {Array} vertices - Array of Vector3 vertices
 * @returns {Array} Vertices in counter-clockwise order
 */
function ensureCounterClockwiseOrder(vertices) {
  // Calculate signed area in XZ plane
  let area = 0;
  const n = vertices.length;
  for (let i = 0; i < n; i++) {
    const vi = vertices[i];
    const vj = vertices[(i + 1) % n];
    area += vi.x * vj.z - vj.x * vi.z;
  }
  const signedAreaXZ = 0.5 * area;

  // Reverse if clockwise
  if (signedAreaXZ < 0) {
    return [...vertices].reverse();
  }
  return vertices;
}

/**
 * Project bottom vertices vertically to create initial top vertices
 * @param {Array} bottomVertices - Array of Vector3 bottom vertices
 * @param {number} projectionPlaneY - Y coordinate of the projection plane
 * @returns {Array} Array of Vector3 top vertices
 */
function projectVerticesToPlane(bottomVertices, projectionPlaneY) {
  const topVertices = [];
  for (const bottomVertex of bottomVertices) {
    const topVertex = new THREE.Vector3(
      bottomVertex.x,
      projectionPlaneY,
      bottomVertex.z
    );
    topVertices.push(topVertex);
  }
  return topVertices;
}

/**
 * Smooth the top vertices to create smooth curves without abrupt changes
 * Uses Laplacian smoothing with configurable iterations and damping
 * @param {Array} vertices - Array of Vector3 vertices to smooth
 * @param {Object} options - Smoothing options
 * @param {number} options.iterations - Number of smoothing iterations (default: 5)
 * @param {number} options.damping - Smoothing damping factor 0-1 (default: 0.5)
 * @returns {Array} Smoothed vertices
 */
function smoothTopVertices(vertices, options = {}) {
  const { iterations = 5, damping = 0.5 } = options;

  if (vertices.length < 3) return vertices;

  // Create a working copy
  let smoothed = vertices.map(v => v.clone());

  // Apply Laplacian smoothing iterations
  for (let iter = 0; iter < iterations; iter++) {
    const newPositions = [];

    for (let i = 0; i < smoothed.length; i++) {
      const prev = smoothed[(i - 1 + smoothed.length) % smoothed.length];
      const curr = smoothed[i];
      const next = smoothed[(i + 1) % smoothed.length];

      // Calculate average position of neighbors
      const avgX = (prev.x + next.x) / 2;
      const avgZ = (prev.z + next.z) / 2;

      // Blend current position with neighbor average
      // Keep Y constant (already projected to plane)
      const newPos = new THREE.Vector3(
        curr.x * (1 - damping) + avgX * damping,
        curr.y, // Y stays constant
        curr.z * (1 - damping) + avgZ * damping
      );

      newPositions.push(newPos);
    }

    smoothed = newPositions;
  }

  return smoothed;
}

/**
 * Create the top mesh from the boundary loop
 * @param {THREE.BufferGeometry} baseGeometry - The original geometry
 * @param {Array} boundaryLoop - Array of vertex indices forming the boundary
 * @param {number} yOffset - Y offset for the projection plane
 * @param {Array} boundaryVertices - Array of all boundary vertex indices
 * @param {Object} options - Options for mesh generation
 * @param {number} options.smoothIterations - Number of smoothing iterations (default: 5)
 * @param {number} options.smoothDamping - Smoothing damping factor 0-1 (default: 0.5)
 * @returns {THREE.BufferGeometry} The generated top mesh
 */
function createTopMeshFromBoundary(baseGeometry, boundaryLoop, yOffset, boundaryVertices = [], options = {}) {
  const positions = baseGeometry.attributes.position.array;

  // Step 1: Extract bottom vertices (boundary loop vertices)
  let bottomVertices = extractBottomVertices(positions, boundaryLoop);

  // Step 2: Ensure loop order is consistent (counter-clockwise in XZ plane)
  bottomVertices = ensureCounterClockwiseOrder(bottomVertices);

  // Step 3: Find topmost point among boundary vertices and create projection plane
  const topmostBoundaryVertex = findTopmostBoundaryVertex(baseGeometry, boundaryVertices);
  const projectionPlaneY = topmostBoundaryVertex.position.y + yOffset;

  // Step 4: Project bottom vertices to create initial top vertices
  let topVertices = projectVerticesToPlane(bottomVertices, projectionPlaneY);

  // Step 5: Smooth top vertices to create nice curves without abrupt changes
  const smoothOptions = {
    iterations: options.smoothIterations ?? 10,
    damping: options.smoothDamping ?? 1
  };
  topVertices = smoothTopVertices(topVertices, smoothOptions);

  // Step 6: Create mesh geometry with bottom vertices, top vertices, and side walls
  const meshOptions = {
    maxEdgeLength: options.maxEdgeLength || 1.0,
    gridSpacing: options.gridSpacing || 0.5,
    sideSubdivisions: options.sideSubdivisions || 3
  };
  return createTopMeshGeometry(bottomVertices, topVertices, meshOptions);
}

/**
 * Create the final top mesh geometry from bottom and top vertex loops using Delaunator
 * @param {Array} bottomVertices - Array of Vector3 for bottom boundary
 * @param {Array} topVertices - Array of Vector3 for top boundary
 * @param {Object} options - Options for triangulation
 * @param {number} options.maxEdgeLength - Maximum edge length for subdivision
 * @param {number} options.gridSpacing - Grid spacing for interior points
 * @param {number} options.sideSubdivisions - Number of subdivisions for side walls (default: 3)
 * @returns {THREE.BufferGeometry} The complete top mesh geometry
 */
function createTopMeshGeometry(bottomVertices, topVertices, options = {}) {
  const { maxEdgeLength = 1.0, gridSpacing = 0.5, sideSubdivisions = 3 } = options;
  const vertexCount = bottomVertices.length;

  // Create arrays for the mesh
  const positions = [];
  const indices = [];

  // Add bottom vertices (indices 0 to vertexCount-1)
  for (const vertex of bottomVertices) {
    positions.push(vertex.x, vertex.y, vertex.z);
  }

  // Add top vertices that will be used by SIDE WALLS
  // Indices: vertexCount to 2*vertexCount-1
  for (const vertex of topVertices) {
    positions.push(vertex.x, vertex.y, vertex.z);
  }

  // Create subdivided side walls connecting bottom and top loops
  const sideWallResult = createSubdividedSideWalls(bottomVertices, topVertices, sideSubdivisions);

  // Add intermediate vertices from subdivision (used only by side walls)
  sideWallResult.newVertices.forEach(vertex => {
    positions.push(vertex.x, vertex.y, vertex.z);
  });

  // Add side wall triangle indices
  indices.push(...sideWallResult.indices);


  // Create the geometry
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);

  // Compute normals and bounds
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();

  return geometry;
}

/**
 * Create top face triangulation using Delaunator algorithm
 * @param {Array} topVertices - Array of Vector3 for top boundary vertices
 * @param {number} topCapStartIndex - Start index of the duplicated top-cap vertices
 * @param {number} newVertexStartIndex - Start index for newly created interior vertices
 * @param {Object} options - Triangulation options
 * @returns {Object} Object with {indices: Array, newVertices: Array}
 */
function createTopFaceWithDelaunator(topVertices, topCapStartIndex, newVertexStartIndex, options = {}) {
  const { maxEdgeLength = 1.0, gridSpacing = 0.5 } = options;

  if (topVertices.length < 3) return { indices: [], newVertices: [] };

  // Step 1: Calculate the centroid and normal of the top face
  const centroid = new THREE.Vector3();
  topVertices.forEach(v => centroid.add(v));
  centroid.divideScalar(topVertices.length);

  // Calculate normal using cross product of consecutive edges
  const normal = new THREE.Vector3();
  for (let i = 0; i < topVertices.length; i++) {
    const a = topVertices[i].clone().sub(centroid);
    const b = topVertices[(i + 1) % topVertices.length].clone().sub(centroid);
    normal.add(new THREE.Vector3().crossVectors(a, b));
  }
  normal.normalize();

  // Step 2: Create a 2D coordinate system on the top face plane
  const tmp = Math.abs(normal.x) > 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
  const u = new THREE.Vector3().crossVectors(tmp, normal).normalize();
  const v = new THREE.Vector3().crossVectors(normal, u).normalize();

  // Step 3: Project top vertices to 2D
  const loop2D = topVertices.map(p => {
    const d = p.clone().sub(centroid);
    return new THREE.Vector2(d.dot(u), d.dot(v));
  });

  // Step 4: Create enriched boundary with subdivided edges
  const enriched = [];
  for (let i = 0; i < topVertices.length; i++) {
    const A3 = topVertices[i];
    const B3 = topVertices[(i + 1) % topVertices.length];
    const A2 = loop2D[i];
    const B2 = loop2D[(i + 1) % loop2D.length];

    // Add the current vertex - reference the duplicated top-cap vertex index
    enriched.push({ p2: A2, p3: A3, isOriginal: true, originalIndex: topCapStartIndex + i });

    // Subdivide edge if it's too long
    const dist = A2.distanceTo(B2);
    const steps = Math.ceil(dist / maxEdgeLength);
    for (let k = 1; k < steps; k++) {
      const t = k / steps;
      const newP2 = new THREE.Vector2(
        A2.x * (1 - t) + B2.x * t,
        A2.y * (1 - t) + B2.y * t
      );
      const newP3 = new THREE.Vector3(
        A3.x * (1 - t) + B3.x * t,
        A3.y * (1 - t) + B3.y * t,
        A3.z * (1 - t) + B3.z * t
      );
      enriched.push({ p2: newP2, p3: newP3, isOriginal: false });
    }
  }

  // Step 5: Add interior grid points
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  loop2D.forEach(p => {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  });

  for (let x = minX; x <= maxX; x += gridSpacing) {
    for (let y = minY; y <= maxY; y += gridSpacing) {
      const pt2 = new THREE.Vector2(x, y);
      if (pointInPolygon(pt2, loop2D)) {
        const p3 = centroid.clone()
          .add(u.clone().multiplyScalar(pt2.x))
          .add(v.clone().multiplyScalar(pt2.y));
        enriched.push({ p2: pt2, p3, isOriginal: false });
      }
    }
  }

  // Step 6: Triangulate using Delaunator
  const pts2 = enriched.map(e => [e.p2.x, e.p2.y]);
  const delaunay = Delaunator.from(pts2);
  const tris = delaunay.triangles;

  // Step 7: Filter triangles and build result
  const finalIndices = [];
  const newVertices = [];
  const newVertexMap = new Map(); // Track new vertex indices

  for (let i = 0; i < tris.length; i += 3) {
    const i0 = tris[i], i1 = tris[i + 1], i2 = tris[i + 2];
    const cx = (pts2[i0][0] + pts2[i1][0] + pts2[i2][0]) / 3;
    const cy = (pts2[i0][1] + pts2[i1][1] + pts2[i2][1]) / 3;

    if (pointInPolygon(new THREE.Vector2(cx, cy), loop2D)) {
      // Get vertex indices for this triangle
      const vi0 = getVertexIndex(enriched[i0], newVertices, newVertexMap, newVertexStartIndex);
      const vi1 = getVertexIndex(enriched[i1], newVertices, newVertexMap, newVertexStartIndex);
      const vi2 = getVertexIndex(enriched[i2], newVertices, newVertexMap, newVertexStartIndex);

      // Check face normal orientation and add triangle
      const vA = enriched[i0].p3, vB = enriched[i1].p3, vC = enriched[i2].p3;
      const faceNormal = new THREE.Vector3()
        .subVectors(vC, vA)
        .cross(new THREE.Vector3().subVectors(vB, vA))
        .normalize();

      if (faceNormal.dot(normal) < 0) {
        finalIndices.push(vi0, vi2, vi1);
      } else {
        finalIndices.push(vi0, vi1, vi2);
      }
    }
  }

  return { indices: finalIndices, newVertices };
}

/**
 * Get vertex index for a vertex, creating a new vertex if needed
 * @param {Object} enrichedVertex - Vertex from enriched array
 * @param {Array} newVertices - Array to add new vertices to
 * @param {Map} newVertexMap - Map to track new vertex indices
 * @param {number} newVertexStartIndex - Start index for the first newly created vertex
 * @returns {number} Vertex index
 */
function getVertexIndex(enrichedVertex, newVertices, newVertexMap, newVertexStartIndex) {
  if (enrichedVertex.isOriginal) {
    return enrichedVertex.originalIndex;
  }

  // Create a key for the vertex position
  const key = `${enrichedVertex.p3.x.toFixed(6)}_${enrichedVertex.p3.y.toFixed(6)}_${enrichedVertex.p3.z.toFixed(6)}`;

  if (newVertexMap.has(key)) {
    return newVertexMap.get(key);
  }

  // Add new vertex at the next available index after the duplicated top-cap loop
  const newIndex = newVertexStartIndex + newVertices.length;
  newVertices.push(enrichedVertex.p3);
  newVertexMap.set(key, newIndex);

  return newIndex;
}

/**
 * Triangulate a loop of vertices using fan triangulation
 * @param {number} vertexCount - Number of vertices in the loop
 * @param {number} startIndex - Starting index for the vertices
 * @param {boolean} flip - Whether to flip the winding order
 * @returns {Array} Array of triangle indices
 */
function triangulateLoop(vertexCount, startIndex, flip = false) {
  const indices = [];

  // Simple fan triangulation from the first vertex
  for (let i = 1; i < vertexCount - 1; i++) {
    if (flip) {
      // Flipped winding for inward-facing normals
      indices.push(
        startIndex,
        startIndex + i + 1,
        startIndex + i
      );
    } else {
      // Normal winding for outward-facing normals
      indices.push(
        startIndex,
        startIndex + i,
        startIndex + i + 1
      );
    }
  }

  return indices;
}

/**
 * Create subdivided side walls connecting the bottom and top loops with intermediate loops
 * @param {Array} bottomVertices - Array of Vector3 for bottom boundary vertices
 * @param {Array} topVertices - Array of Vector3 for top boundary vertices
 * @param {number} subdivisions - Number of intermediate subdivisions (default: 3)
 * @returns {Object} Object with {indices: Array, newVertices: Array}
 */
function createSubdividedSideWalls(bottomVertices, topVertices, subdivisions = 3) {
  const vertexCount = bottomVertices.length;
  const indices = [];
  const newVertices = [];

  // Calculate total number of loops (bottom + subdivisions + top)
  const totalLoops = subdivisions + 2;

  // Create intermediate loop vertices
  const loops = [];

  // Add bottom loop (existing vertices, use indices 0 to vertexCount-1)
  loops.push(Array.from({ length: vertexCount }, (_, i) => i));

  // Create intermediate subdivision loops
  for (let loop = 1; loop <= subdivisions; loop++) {
    const t = loop / (totalLoops - 1); // Interpolation factor from 0 to 1
    const loopIndices = [];

    for (let i = 0; i < vertexCount; i++) {
      const bottomVert = bottomVertices[i];
      const topVert = topVertices[i];

      // Linear interpolation between bottom and top vertices
      const interpolatedVertex = new THREE.Vector3(
        bottomVert.x * (1 - t) + topVert.x * t,
        bottomVert.y * (1 - t) + topVert.y * t,
        bottomVert.z * (1 - t) + topVert.z * t
      );

      newVertices.push(interpolatedVertex);
      // Index will be: vertexCount * 2 + newVertices.length - 1
      loopIndices.push(vertexCount * 2 + newVertices.length - 1);
    }

    loops.push(loopIndices);
  }

  // Add top loop (existing vertices, use indices vertexCount to 2*vertexCount-1)
  loops.push(Array.from({ length: vertexCount }, (_, i) => vertexCount + i));

  // Create triangulated side walls between consecutive loops
  for (let loopIdx = 0; loopIdx < loops.length - 1; loopIdx++) {
    const currentLoop = loops[loopIdx];
    const nextLoop = loops[loopIdx + 1];

    for (let i = 0; i < vertexCount; i++) {
      const next = (i + 1) % vertexCount;

      // Current edge vertices
      const v0 = currentLoop[i];      // current loop, vertex i
      const v1 = currentLoop[next];   // current loop, vertex i+1
      const v2 = nextLoop[i];         // next loop, vertex i
      const v3 = nextLoop[next];      // next loop, vertex i+1

      // Create two triangles for the quad with correct winding for outward normals
      // Triangle 1: v0, v2, v3 (counter-clockwise when viewed from outside)
      indices.push(v0, v2, v3);
      // Triangle 2: v3, v1, v0 (counter-clockwise when viewed from outside)
      indices.push(v3, v1, v0);
    }
  }

  return { indices, newVertices };
}

/**
 * Create side walls connecting the bottom and top loops (legacy function, kept for compatibility)
 * @param {number} vertexCount - Number of vertices in each loop
 * @returns {Array} Array of triangle indices for side walls
 */
function createSideWalls(vertexCount) {
  const indices = [];
  const bottomOffset = 0;
  const topOffset = vertexCount;

  for (let i = 0; i < vertexCount; i++) {
    const next = (i + 1) % vertexCount;

    // Current vertices
    const vb = bottomOffset + i;       // vb_i
    const vbNext = bottomOffset + next; // vb_{i+1}
    const vt = topOffset + i;          // vt_i
    const vtNext = topOffset + next;   // vt_{i+1}

    // Reversed winding order for correct outward-facing normals:
    // Face 1: vb_i, vt_i, vt_{i+1}
    indices.push(vb, vt, vtNext);
    // Face 2: vt_{i+1}, vb_{i+1}, vb_i
    indices.push(vtNext, vbNext, vb);
  }

  return indices;
}

/**
 * Validate if a geometry represents a valid manifold mesh
 * @param {THREE.BufferGeometry} geometry - Geometry to validate
 * @returns {boolean} True if the mesh appears to be manifold
 */
export function validateManifoldMesh(geometry) {
  if (!geometry || !geometry.attributes.position) {
    return false;
  }

  const positions = geometry.attributes.position.array;
  const indices = geometry.index ? geometry.index.array : null;

  if (!indices) {
    console.warn('Cannot validate non-indexed geometry');
    return false;
  }

  // Check if all triangles are valid (no degenerate triangles)
  for (let i = 0; i < indices.length; i += 3) {
    const a = indices[i];
    const b = indices[i + 1];
    const c = indices[i + 2];

    // Check for degenerate triangles (same vertex indices)
    if (a === b || b === c || c === a) {
      return false;
    }

    // Check vertex positions for degenerate triangles (same positions)
    const va = new THREE.Vector3(positions[a * 3], positions[a * 3 + 1], positions[a * 3 + 2]);
    const vb = new THREE.Vector3(positions[b * 3], positions[b * 3 + 1], positions[b * 3 + 2]);
    const vc = new THREE.Vector3(positions[c * 3], positions[c * 3 + 1], positions[c * 3 + 2]);

    const area = triangleArea(va, vb, vc);
    if (area < 1e-10) {
      return false;
    }
  }

  return true;
}

/**
 * Calculate the area of a triangle
 * @param {THREE.Vector3} a - First vertex
 * @param {THREE.Vector3} b - Second vertex
 * @param {THREE.Vector3} c - Third vertex
 * @returns {number} Triangle area
 */
function triangleArea(a, b, c) {
  const ab = new THREE.Vector3().subVectors(b, a);
  const ac = new THREE.Vector3().subVectors(c, a);
  const cross = new THREE.Vector3().crossVectors(ab, ac);
  return cross.length() * 0.5;
}

/**
 * Merge cap geometry from fixedGeometry into the top mesh
 * The fixedGeometry contains cap triangles that need to be included
 * @param {THREE.BufferGeometry} fixedGeometry - Geometry with cap faces added
 * @param {THREE.BufferGeometry} topMesh - The generated top mesh
 * @param {THREE.BufferGeometry} baseGeometry - Original base geometry (for vertex count)
 * @returns {THREE.BufferGeometry} Merged geometry
 */
function mergeCapGeometryIntoTopMesh(fixedGeometry, topMesh, baseGeometry) {
  const baseVertexCount = baseGeometry.attributes.position.count;
  const fixedIndices = fixedGeometry.index ? fixedGeometry.index.array : null;
  const fixedPositions = fixedGeometry.attributes.position.array;

  if (!fixedIndices) {
    console.warn('[TopMeshGenerator] Fixed geometry is not indexed, cannot merge caps');
    return topMesh;
  }

  // Extract cap faces (faces that use vertices beyond the base geometry)
  const capFaces = [];
  for (let i = 0; i < fixedIndices.length; i += 3) {
    const i0 = fixedIndices[i];
    const i1 = fixedIndices[i + 1];
    const i2 = fixedIndices[i + 2];

    // If any vertex index is beyond base geometry, it's a cap face
    if (i0 >= baseVertexCount || i1 >= baseVertexCount || i2 >= baseVertexCount) {
      capFaces.push([i0, i1, i2]);
    }
  }

  if (capFaces.length === 0) {
    console.log('[TopMeshGenerator] No cap faces found to merge');
    return topMesh;
  }

  console.log(`[TopMeshGenerator] Merging ${capFaces.length} cap faces into top mesh`);

  // Get existing top mesh data
  const topPositions = Array.from(topMesh.attributes.position.array);
  const topIndices = topMesh.index ? Array.from(topMesh.index.array) : [];
  const topVertexCount = topPositions.length / 3;

  // Build vertex map for cap vertices
  const capVertexMap = new Map(); // fixedGeometry index -> topMesh index
  let newVertexCount = topVertexCount;

  // Add cap vertices to top mesh
  capFaces.forEach(([i0, i1, i2]) => {
    [i0, i1, i2].forEach(fixedIdx => {
      if (!capVertexMap.has(fixedIdx)) {
        // Add this vertex to topPositions
        topPositions.push(
          fixedPositions[fixedIdx * 3],
          fixedPositions[fixedIdx * 3 + 1],
          fixedPositions[fixedIdx * 3 + 2]
        );
        capVertexMap.set(fixedIdx, newVertexCount);
        newVertexCount++;
      }
    });
  });

  // Add cap faces to top mesh indices
  capFaces.forEach(([i0, i1, i2]) => {
    topIndices.push(
      capVertexMap.get(i0),
      capVertexMap.get(i1),
      capVertexMap.get(i2)
    );
  });

  // Create merged geometry
  const mergedGeometry = new THREE.BufferGeometry();
  mergedGeometry.setAttribute('position', new THREE.Float32BufferAttribute(topPositions, 3));
  mergedGeometry.setIndex(topIndices);

  // Weld coincident seam vertices so normals can be averaged across cap/side faces
  const tolerance = 1e-4;
  const deduped = BufferGeometryUtils.mergeVertices(mergedGeometry, tolerance);
  deduped.computeVertexNormals();
  deduped.computeBoundingSphere();

  console.log(`[TopMeshGenerator] Merged (deduped) geometry: ${deduped.attributes.position.count} vertices, ${deduped.index ? deduped.index.count / 3 : deduped.attributes.position.count / 9} triangles`);

  return deduped;
}

/**
 * Get top mesh generation statistics
 * @param {THREE.BufferGeometry} topMesh - Generated top mesh
 * @returns {Object} Statistics object
 */
export function getTopMeshStats(topMesh) {
  if (!topMesh) {
    return { valid: false };
  }

  const positions = topMesh.attributes.position.array;
  const indices = topMesh.index ? topMesh.index.array : null;

  return {
    valid: true,
    vertexCount: positions.length / 3,
    triangleCount: indices ? indices.length / 3 : positions.length / 9,
    hasNormals: !!topMesh.attributes.normal,
    hasBoundingSphere: !!topMesh.boundingSphere,
    isManifold: validateManifoldMesh(topMesh)
  };
}